/**
 * Wave 29 Chunk 10i — Sales-doc OCR Scanner panel.
 *
 * Mounted on /customers/:cid/sales-docs/new (above the audience picker).
 * The owner drops a quote / SO / invoice file (PDF or image), Hidro OCRs
 * it via GPT-5.2 Vision, every line item is fuzzy-matched against the
 * 17k inventory rows, and the panel returns an `onApply(suggestion)`
 * callback the parent uses to pre-fill the form.
 *
 * Features:
 *   - drag-drop OR click-to-pick
 *   - auto-detects doc type (quote / invoice / sales_order / credit_note)
 *   - matches bill-to → customer (warns if it doesn't match the page customer)
 *   - per-line inventory match table with confidence + price-delta flag
 *   - per-line action: accept / override / ignore
 *   - inline price override modal: "Update master price too?"
 */
import { useRef, useState, useCallback } from "react";
import {
  Upload, Loader2, ScanText, FileWarning, Check, X as XIcon,
  AlertTriangle, Sparkles, ShieldCheck, ChevronDown, Edit3,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "../components/ui/sonner";

export default function SalesDocScanner({
  customerId,
  initialCategory,
  onApply,
  onFilePicked,        // Iter 290r — parent picks up the actual file so submit uploads it
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);  // OCR result from backend
  const [err, setErr] = useState("");
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);

  const runScan = useCallback(async (f) => {
    if (!f) return;
    setErr("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (customerId) fd.append("customer_id", customerId);
      if (initialCategory) fd.append("category", initialCategory);
      const { data } = await api.post("/sales-documents/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setScan(data);
      toast.success(`Hidro extracted ${(data.lines || []).length} line(s) from the doc.`);
    } catch (e) {
      const m = formatErr(e?.response?.data?.detail) || "OCR failed.";
      setErr(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }, [customerId, initialCategory]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    onFilePicked?.(f);
    runScan(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    setFile(f);
    onFilePicked?.(f);
    runScan(f);
  };

  // Update a single line's action / matched_inventory / unit_price.
  const setLine = (idx, patch) => {
    setScan((s) => ({
      ...s,
      lines: s.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };

  const reset = () => { setScan(null); setFile(null); setErr(""); };

  const apply = () => {
    if (!scan) return;
    // Build a normalised suggestion the parent can spread into its form state.
    const total = (() => {
      const t = scan.totals?.total;
      if (typeof t === "number") return t;
      // Sum accepted lines if total missing.
      return (scan.lines || []).reduce((sum, l) => (
        l.action !== "ignore" ? sum + (Number(l.line_total) || 0) : sum
      ), 0);
    })();
    const suggestion = {
      category: scan.suggested_category,
      document_number: scan.document_number || "",
      document_date: scan.document_date || "",
      amount: total || null,
      currency: scan.currency || "ZAR",
      title: scan.document_number
        ? `${(scan.suggested_category || "doc").replace("_", " ")} ${scan.document_number}`
        : "",
      // Pass through OCR'd line items so the parent can persist them in `line_items`.
      line_items: (scan.lines || [])
        .filter((l) => l.action !== "ignore")
        .map((l) => ({
          raw_text: l.raw_text,
          name: l.name,
          sku: l.matched_sku || l.sku,
          quantity: l.quantity,
          unit: l.unit,
          unit_price: l.unit_price,
          line_total: l.line_total,
          matched_inventory_id: l.matched_inventory_id,
          matched_inventory_name: l.matched_inventory_name,
          matched_confidence: l.matched_confidence,
          matched_avg_cost: l.matched_avg_cost,
          matched_master_price: l.matched_master_price,
          matched_sku: l.matched_sku,
          price_delta_pct: l.price_delta_pct,
          action: l.action,
        })),
      bill_to_match: scan.customer_match,
      bill_to_raw: scan.bill_to,
    };
    onApply?.(suggestion);
    toast.success("Form pre-filled. Review the fields and save.");
  };

  // Price-override modal local state
  const [overrideTarget, setOverrideTarget] = useState(null); // {idx, line}
  const submitOverride = async (newPrice, reason) => {
    if (!overrideTarget) return;
    const inv_id = overrideTarget.line.matched_inventory_id;
    try {
      await api.post(`/inventory/${inv_id}/price-override`, {
        new_price: Number(newPrice),
        reason: reason || "Adjusted from sales-doc OCR",
      });
      // Also reflect on the line so the table updates.
      setLine(overrideTarget.idx, {
        matched_master_price: Number(newPrice),
        price_delta_pct: 0,
        price_flag: "match",
      });
      toast.success("Master price updated.");
      setOverrideTarget(null);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Price update failed.");
    }
  };

  return (
    <section className={`hv-card !p-4 space-y-3 border-[#1FB6C1]/30 ${className}`} data-testid="sales-doc-scanner">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <ScanText className="w-4 h-4 text-[#1FB6C1]" /> Scan & auto-fill
          <span className="text-[10px] text-[#647087] font-normal hidden sm:inline">
            · Hidro reads the doc, matches lines to inventory, you confirm.
          </span>
        </h2>
        {scan && (
          <button type="button" onClick={reset} className="hv-btn hv-btn-ghost text-[10px]" data-testid="sales-doc-scanner-reset">
            <XIcon className="w-3 h-3" /> Start over
          </button>
        )}
      </header>

      {!scan && !busy && (
        <label
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          className="block border-2 border-dashed border-white/15 hover:border-[#1FB6C1]/60 transition rounded-lg p-6 cursor-pointer text-center"
          data-testid="sales-doc-scanner-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={onPick}
            className="hidden"
            data-testid="sales-doc-scanner-file-input"
          />
          <Upload className="w-7 h-7 text-[#647087] mx-auto mb-2" />
          <div className="text-sm text-white">Drop a quote / sales order / invoice here</div>
          <div className="text-[10px] text-[#647087] mt-1">PDF or image · max 20 MB · processed by GPT-5.2 Vision</div>
          <button type="button" onClick={() => inputRef.current?.click()} className="hv-btn hv-btn-primary text-xs mt-3">
            <Upload className="w-3 h-3" /> Choose file
          </button>
        </label>
      )}

      {busy && (
        <div className="rounded-lg border border-[#1FB6C1]/30 bg-[#1FB6C1]/[0.05] p-4 flex items-center gap-3" data-testid="sales-doc-scanner-busy">
          <Loader2 className="w-5 h-5 text-[#1FB6C1] animate-spin" />
          <div className="text-xs text-[#dde3ec]">
            <div className="font-semibold">Hidro is reading {file?.name || "the document"}…</div>
            <div className="text-[10px] text-[#647087]">Vision pass + inventory match · usually 10–25 s</div>
          </div>
        </div>
      )}

      {err && (
        <div className="text-xs text-rose-300 border border-rose-400/30 rounded p-2 flex items-center gap-2" data-testid="sales-doc-scanner-error">
          <FileWarning className="w-4 h-4" /> {err}
        </div>
      )}

      {scan && (
        <div className="space-y-3" data-testid="sales-doc-scanner-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <Pill label="Doc type" value={(scan.suggested_category || scan.doc_type || "—").replace("_", " ")} />
            <Pill label="Number" value={scan.document_number || "—"} mono />
            <Pill label="Date" value={scan.document_date || "—"} />
            <Pill label="Total" value={scan.totals?.total != null ? `${scan.currency || "R"} ${Number(scan.totals.total).toFixed(2)}` : "—"} mono />
          </div>

          {/* Bill-to / customer match warning */}
          {scan.customer_match?.matched_customer_id && customerId && scan.customer_match.matched_customer_id !== customerId && (
            <div className="text-[11px] rounded border border-amber-400/40 bg-amber-400/10 text-amber-200 p-2 flex items-start gap-2" data-testid="sales-doc-scanner-customer-mismatch">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                The OCR'd bill-to ("{scan.bill_to?.name}") best matches <b>{scan.customer_match.matched_customer_name}</b> at{" "}
                {Math.round((scan.customer_match.matched_confidence || 0) * 100)}% confidence, NOT the customer you're on.
                Double-check the file isn't for a different account.
              </div>
            </div>
          )}

          {/* Line items */}
          {(scan.lines || []).length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="sales-doc-scanner-lines">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/10">
                    <th className="text-left py-1.5 px-1 font-medium">Line</th>
                    <th className="text-left py-1.5 px-1 font-medium">Match</th>
                    <th className="text-right py-1.5 px-1 font-medium">Qty</th>
                    <th className="text-right py-1.5 px-1 font-medium">OCR price</th>
                    <th className="text-right py-1.5 px-1 font-medium">Master</th>
                    <th className="text-right py-1.5 px-1 font-medium">Δ</th>
                    <th className="text-left py-1.5 px-1 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {scan.lines.map((l, idx) => {
                    const conf = Math.round((l.matched_confidence || 0) * 100);
                    const flagged = l.price_flag === "flag_5pct";
                    return (
                      <tr key={idx} className="border-b border-white/5 align-top" data-testid={`sales-doc-scanner-line-${idx}`}>
                        <td className="py-1.5 px-1 max-w-[200px]">
                          <div className="text-white truncate">{l.name || l.raw_text}</div>
                          {l.sku && <div className="text-[10px] text-[#647087] font-mono">SKU {l.sku}</div>}
                        </td>
                        <td className="py-1.5 px-1 max-w-[200px]">
                          {l.matched_inventory_id ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-emerald-300 truncate flex items-center gap-1">
                                <ShieldCheck className="w-3 h-3" /> {l.matched_inventory_name}
                              </span>
                              <span className="text-[10px] text-[#647087]">{conf}% · {l.matched_sku || "—"}</span>
                            </div>
                          ) : (
                            <span className="text-amber-300 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> No SKU match
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 px-1 text-right font-mono">{l.quantity ?? "—"}</td>
                        <td className="py-1.5 px-1 text-right font-mono">{l.unit_price != null ? Number(l.unit_price).toFixed(2) : "—"}</td>
                        <td className="py-1.5 px-1 text-right font-mono text-[#a0aabf]">{l.matched_master_price != null ? Number(l.matched_master_price).toFixed(2) : "—"}</td>
                        <td className={`py-1.5 px-1 text-right font-mono ${flagged ? "text-rose-300" : "text-[#647087]"}`}>
                          {l.price_delta_pct != null ? `${l.price_delta_pct > 0 ? "+" : ""}${l.price_delta_pct}%` : "—"}
                        </td>
                        <td className="py-1.5 px-1">
                          <div className="flex flex-wrap items-center gap-1">
                            <ActionPill active={l.action === "accept"} onClick={() => setLine(idx, { action: "accept" })} testId={`line-${idx}-accept`}>
                              <Check className="w-3 h-3" /> Accept
                            </ActionPill>
                            <ActionPill active={l.action === "ignore"} onClick={() => setLine(idx, { action: "ignore" })} testId={`line-${idx}-ignore`}>
                              <XIcon className="w-3 h-3" /> Ignore
                            </ActionPill>
                            {flagged && l.matched_inventory_id && (
                              <button
                                type="button"
                                onClick={() => setOverrideTarget({ idx, line: l })}
                                className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
                                data-testid={`line-${idx}-override`}
                              >
                                <Edit3 className="w-3 h-3" /> Update master
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={reset} className="hv-btn hv-btn-ghost text-xs" data-testid="sales-doc-scanner-cancel">
              Discard scan
            </button>
            <button type="button" onClick={apply} className="hv-btn hv-btn-primary text-xs" data-testid="sales-doc-scanner-apply">
              <Sparkles className="w-3 h-3" /> Apply to form
            </button>
          </div>
        </div>
      )}

      {overrideTarget && (
        <PriceOverrideModal
          target={overrideTarget}
          onCancel={() => setOverrideTarget(null)}
          onConfirm={submitOverride}
        />
      )}
    </section>
  );
}

function Pill({ label, value, mono }) {
  return (
    <div className="border border-white/10 rounded px-2 py-1 bg-white/[0.02]">
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className={`text-white truncate ${mono ? "font-mono" : ""}`} title={String(value)}>{value}</div>
    </div>
  );
}

function ActionPill({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition ${
        active ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-200" : "border-white/15 text-[#a0aabf] hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}

function PriceOverrideModal({ target, onCancel, onConfirm }) {
  const { line } = target;
  const [newPrice, setNewPrice] = useState(String(line.unit_price ?? ""));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="price-override-modal" data-hv-modal-portal="true">
      <div className="hv-card !p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Edit3 className="w-4 h-4 text-amber-300" /> Update master price
        </h3>
        <div className="text-xs text-[#dde3ec] space-y-1">
          <div><span className="text-[#647087]">SKU:</span> <span className="font-mono">{line.matched_sku}</span></div>
          <div><span className="text-[#647087]">Item:</span> {line.matched_inventory_name}</div>
          <div><span className="text-[#647087]">Master price:</span> <span className="font-mono">R {Number(line.matched_master_price || 0).toFixed(2)}</span></div>
          <div><span className="text-[#647087]">OCR price:</span> <span className="font-mono">R {Number(line.unit_price || 0).toFixed(2)}</span></div>
        </div>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">New master price (excl. VAT)</div>
          <input type="number" step="0.01" value={newPrice} onChange={(e) => setNewPrice(e.target.value)}
            className="hv-input text-sm w-full font-mono" data-testid="price-override-new-price" />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Reason (audit log)</div>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. supplier raised cost, retail re-price, OCR correction"
            className="hv-input text-sm w-full" data-testid="price-override-reason" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="hv-btn hv-btn-ghost text-xs" data-testid="price-override-cancel">Cancel</button>
          <button
            type="button"
            disabled={busy || !newPrice || !reason.trim()}
            onClick={async () => { setBusy(true); await onConfirm(newPrice, reason); setBusy(false); }}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="price-override-confirm"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Update master price
          </button>
        </div>
      </div>
    </div>
  );
}
